/**
 * RetreatDetailScreen — ScreenPreview design.
 *
 * UI-INV-005: Leaf component, props only.
 * screen-integration-006
 *
 * When `templateId` is present the schedule is generated dynamically via
 * useScheduleGenerator and the result is passed to onStartNow. When absent
 * (skipWizard=true or no templateId) the static `schedule` prop is used
 * and the component degrades gracefully — no warnings, no adjust button.
 */
import { useState } from "react";
import type { EditableRetreatSchedule, BellPatternId } from "@retreat/core";
import { bellPatternLabel } from "../data/bellPatternLabels";
import { totalSeconds, formatDuration, displayScheduleName, fmtClock } from "../utils/scheduleUtils";
import { useScheduleGenerator } from "../hooks/useScheduleGenerator";
import { getTemplate } from "@retreats/content";
import { ScheduleTimeline } from "../components/ScheduleTimeline";
import { AdjustPreferencesSheet } from "../components/AdjustPreferencesSheet";
import styles from "./RetreatDetailScreen.module.css";
import { TEST_IDS } from "../testing/testIds";
import { PrimaryAction } from "../primitives/PrimaryAction";
import { SecondaryAction } from "../primitives/SecondaryAction";

function formatWakeHour(hour: number): string {
  const h = Math.floor(hour);
  const m = Math.round((hour % 1) * 60);
  const period = h < 12 ? "AM" : "PM";
  const display = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${display}:${m.toString().padStart(2, "0")} ${period}`;
}

type Props = {
  schedule: EditableRetreatSchedule;
  templateId?: string;
  bellPatterns: BellPatternId[];
  onStartNow: (s: EditableRetreatSchedule) => void;
  onSchedule: () => void;
  onPlaySignal: (patternId: BellPatternId) => void;
  onBack: () => void;
  /** Opens ScheduleEditorScreen pre-populated with this schedule (forked copy). */
  onEdit?: () => void;
};

export function RetreatDetailScreen({
  schedule,
  templateId,
  bellPatterns,
  onStartNow,
  onSchedule,
  onPlaySignal,
  onBack,
  onEdit,
}: Props) {
  const [adjustOpen, setAdjustOpen] = useState(false);

  // Dynamic path: templateId present → use generator
  const nowMs = Date.now();
  const startDateTime = new Date(nowMs);

  const {
    schedule: generatedSchedule,
    warnings,
    preferences,
    regenerate,
  } = useScheduleGenerator(templateId, startDateTime, undefined, schedule);

  // Use generated schedule when available; fall back to static prop
  const useGenerator = !!templateId;
  const displaySchedule = useGenerator ? generatedSchedule : schedule;

  // Template metadata (for noble silence)
  let template = null;
  try {
    if (templateId) template = getTemplate(templateId);
  } catch {
    // unknown templateId — graceful fallback
  }
  const hasNoSilence = template?.noblesilence === true;
  const tradition = (template?.tradition ?? "zen") as "zen" | "vipassana";
  const defaultWakeHour = template?.defaultWakeHour ?? 9;

  const name = displayScheduleName(displaySchedule);
  const total = totalSeconds(displaySchedule);
  const days = displaySchedule.days ?? [];
  const isMultiDay = days.length > 1;

  const endMs = nowMs + total * 1000;
  const nowStr = fmtClock(nowMs);
  const endStr = fmtClock(endMs);

  // Static schedule rendering (fallback for non-generator path)
  let globalOffset = 0;
  const dayRows = days.map((day, dayIdx) => {
    const containers = day.containers ?? [];
    const rows = containers.map((c) => {
      const startStr = fmtClock(nowMs + globalOffset * 1000);
      globalOffset += c.durationSeconds;
      return { c, startStr };
    });
    return { dayIdx, containers, rows };
  });

  const totalContainers = days.reduce((s, d) => s + (d.containers?.length ?? 0), 0);

  return (
    <div className={styles.screen} data-testid={TEST_IDS.RETREAT_DETAIL_SCREEN}>
      {/* Top meta bar */}
      <div className={styles.meta}>
        <button className={styles.backBtn} onClick={onBack} type="button">
          ← back
        </button>
        <div className={styles.metaRight}>
          {onEdit && (
            <button
              className={styles.editBtn}
              onClick={onEdit}
              type="button"
              data-testid={TEST_IDS.RETREAT_DETAIL_EDIT_BTN}
            >
              Edit →
            </button>
          )}
          <span className={styles.metaLabel}>Preview</span>
          {useGenerator && (
            <button
              className={styles.adjustBtn}
              onClick={() => setAdjustOpen(true)}
              type="button"
              data-testid={TEST_IDS.RETREAT_DETAIL_ADJUST_BTN}
            >
              Adjust
            </button>
          )}
        </div>
      </div>

      {/* Scrollable body */}
      <div className={styles.content}>
        {/* Hero — always use static schedule for narrative copy */}
        <h1 className={styles.heading}>{name}</h1>
        <p className={styles.description}>
          {(schedule as { summary?: string }).summary ?? "A complete retreat."}
        </p>
        <div className={styles.metaRow}>
          <span>{formatDuration(total)}</span>
          {isMultiDay && <><span>·</span><span>{days.length} days</span></>}
        </div>

        {/* You'll need — shown before schedule */}
        <div className={styles.needBlock}>
          <div className={styles.needLabel}>Before you begin</div>
          {hasNoSilence && (
            <div
              className={styles.nobleSilenceRow}
              data-testid={TEST_IDS.RETREAT_DETAIL_NOBLE_SILENCE}
            >
              🔇 Noble silence throughout
            </div>
          )}
          <p className={styles.needText}>
            {(schedule as { requirements?: string }).requirements ??
              "A quiet corner. A cushion or chair. Water. A simple meal if the retreat includes one."}
          </p>
        </div>

        {/* Multi-day explainer */}
        {isMultiDay && (
          <div className={styles.multiDayNote} data-testid={TEST_IDS.RETREAT_DETAIL_MULTIDAY_NOTE}>
            <div className={styles.needLabel}>How overnight retreats work</div>
            <ul className={styles.multiDayList}>
              <li>Sleep sessions show a dim idle screen — the schedule continues in the background.</li>
              <li>The bell will ring at each morning sitting even if your phone was locked overnight.</li>
              <li>If you leave the app, open it again to return to wherever the retreat is.</li>
              <li>Install to your home screen before you begin — essential for overnight reliability.</li>
            </ul>
          </div>
        )}

        {/* If you begin now */}
        <div className={styles.nowBox}>
          <div className={styles.nowLabel}>
            {isMultiDay ? `If you begin now · ${days.length}-day retreat` : "If you begin now"}
          </div>
          <div className={styles.nowTime}>{nowStr} → {endStr}</div>
        </div>

        {/* Warning banner — one banner per schedule, not per container */}
        {useGenerator && warnings.length > 0 && (
          <div
            className={styles.warningBanner}
            data-testid={TEST_IDS.RETREAT_DETAIL_WARNING_BANNER}
          >
            <div className={styles.warningTitle}>⚠ Timing</div>
            <p className={styles.warningBody}>
              Starting now means meals fall at unusual hours.
            </p>
            <button
              className={styles.warningCta}
              onClick={onSchedule}
              type="button"
              data-testid={TEST_IDS.ADJUST_SHEET_START_TOMORROW}
            >
              Start tomorrow at {formatWakeHour(defaultWakeHour)} instead →
            </button>
          </div>
        )}

        {/* Signals — tradition-specific bell preview */}
        {bellPatterns.length > 0 && (
          <div className={styles.signalsSection} data-testid={TEST_IDS.RETREAT_DETAIL_SIGNALS}>
            <div className={styles.sectionLabel}>Signals in this retreat</div>
            {bellPatterns.map((patternId) => (
              <div key={patternId} className={styles.signalRow}>
                <span className={styles.signalLabel}>{bellPatternLabel(patternId)}</span>
                <button
                  className={styles.signalPlayBtn}
                  onClick={() => onPlaySignal(patternId)}
                  type="button"
                  aria-label={`Hear ${bellPatternLabel(patternId)}`}
                  data-testid={TEST_IDS.RETREAT_DETAIL_SIGNAL_BTN}
                >
                  ▶
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Schedule — dynamic timeline when generator available, static otherwise */}
        <div className={styles.sectionLabel}>
          {isMultiDay ? `Full schedule · ${days.length} days` : "Today's schedule"}
        </div>

        {useGenerator ? (
          <ScheduleTimeline
            schedule={displaySchedule}
            startDateTime={startDateTime}
            warnings={[]}
          />
        ) : (
          <div className={styles.scheduleList}>
            {dayRows.map(({ dayIdx, rows }) => (
              <div key={dayIdx}>
                {isMultiDay && (
                  <div className={styles.dayHeader}>
                    <span className={styles.dayNum}>Day {dayIdx + 1}</span>
                    <span className={styles.dayOf}>of {days.length}</span>
                  </div>
                )}
                {rows.map(({ c, startStr }, i) => (
                  <div key={i} className={styles.scheduleRow}>
                    <span className={styles.rowLeft}>
                      {c.name}
                      {c.traditionTerm && (
                        <span className={styles.rowTerm}> · {c.traditionTerm}</span>
                      )}
                    </span>
                    <span className={styles.rowRight}>
                      {startStr} · {formatDuration(c.durationSeconds)}
                    </span>
                  </div>
                ))}
              </div>
            ))}
            {isMultiDay && (
              <div className={styles.totalRow}>{totalContainers} sessions total</div>
            )}
          </div>
        )}
      </div>

      {/* Sticky footer CTAs */}
      <div className={styles.ctas}>
        <PrimaryAction
          onClick={() => onStartNow(displaySchedule)}
          testId={TEST_IDS.DETAIL_START_NOW_BTN}
        >
          Begin now
        </PrimaryAction>
        <SecondaryAction onClick={onSchedule} testId={TEST_IDS.DETAIL_SCHEDULE_BTN}>
          Schedule for later
        </SecondaryAction>
      </div>

      {/* Adjust preferences sheet */}
      {useGenerator && (
        <AdjustPreferencesSheet
          open={adjustOpen}
          preferences={preferences}
          tradition={tradition}
          defaultWakeHour={defaultWakeHour}
          isStartNowMode={true}
          onPreferencesChange={regenerate}
          onClose={() => setAdjustOpen(false)}
          onStartTomorrow={onSchedule}
        />
      )}
    </div>
  );
}
